import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, AlertCircle, Mail, ArrowLeft, Loader2, Search, Coins, Compass } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const INTENTS = [
  { value: "get_tasks_done", label: "Get tasks done", Icon: Search, desc: "Find executors" },
  { value: "earn", label: "Earn by doing tasks", Icon: Coins, desc: "Execute & earn" },
  { value: "explore", label: "Explore", Icon: Compass, desc: "Browse the platform" },
];

export default function RegisterPage() {
  const location = useLocation();
  const [step, setStep] = useState("info"); // "info" | "otp"
  const [form, setForm] = useState({ email: location.state?.email || "", full_name: "", intent: "explore" });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const requestOTP = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { setError("Full name is required"); return; }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/request-otp", {
        email: form.email,
        full_name: form.full_name,
        intent: form.intent,
      });
      setStep("otp");
      setCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (val && idx === 5 && next.every((d) => d)) {
      verifyOTP(next.join(""));
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
      setTimeout(() => verifyOTP(pasted), 100);
      e.preventDefault();
    }
  };

  const verifyOTP = async (code) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/verify-otp", { email: form.email, code });
      setUser(data);
      if (form.intent === "earn") navigate("/earn");
      else if (form.intent === "get_tasks_done") navigate("/search?q=get+more+customers");
      else navigate("/");
    } catch (err) {
      setError(formatError(err));
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/api/auth/request-otp", { email: form.email, full_name: form.full_name, intent: form.intent });
      setCooldown(60);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 24px 40px", position: "relative", overflow: "hidden" }}>
      <div className="bg-blob" style={{ width: 350, height: 350, background: "rgba(59,130,246,0.04)", top: "5%", right: "-5%", animationDelay: "0s" }} />
      <div className="bg-blob" style={{ width: 280, height: 280, background: "rgba(233,238,245,0.4)", bottom: "10%", left: "-5%", animationDelay: "5s" }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 2 }}>
        <div className="glass-card" style={{ padding: "2.2rem" }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
              <Zap size={18} color="white" strokeWidth={1.5} />
            </div>
            <h1 data-testid="register-heading" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.4rem", letterSpacing: "-0.03em", color: "var(--text-main)", marginBottom: "0.3rem" }}>
              {step === "info" ? "Create account" : "Enter verification code"}
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", fontFamily: "'Manrope', sans-serif" }}>
              {step === "info" ? "Join TiE — the task execution network" : (
                <>Code sent to <strong style={{ color: "var(--text-main)" }}>{form.email}</strong></>
              )}
            </p>
          </div>

          {error && (
            <div data-testid="register-error" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(181,90,90,0.06)", border: "1px solid rgba(181,90,90,0.15)", borderRadius: 10, padding: "0.65rem 0.9rem", marginBottom: "1.2rem" }}>
              <AlertCircle size={14} color="#B55A5A" strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: "0.8rem", color: "#B55A5A", fontFamily: "'Manrope', sans-serif" }}>{error}</span>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === "info" ? (
              <motion.form key="info-step" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                onSubmit={requestOTP}>
                <div style={{ marginBottom: "1rem" }}>
                  <label className="form-label">Full name</label>
                  <input data-testid="register-name-input" type="text" className="form-input" value={form.full_name} onChange={set("full_name")} placeholder="Your name" required />
                </div>
                <div style={{ marginBottom: "1.2rem" }}>
                  <label className="form-label">Email</label>
                  <div style={{ position: "relative" }}>
                    <Mail size={15} color="var(--text-muted)" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                    <input data-testid="register-email-input" type="email" className="form-input" value={form.email} onChange={set("email")} placeholder="you@example.com" required style={{ paddingLeft: 36 }} />
                  </div>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label className="form-label" style={{ marginBottom: 8 }}>What are you here for?</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {INTENTS.map(({ value, label, Icon }) => (
                      <div key={value} data-testid={`intent-${value}`} onClick={() => setForm((f) => ({ ...f, intent: value }))}
                        style={{
                          border: `1.5px solid ${form.intent === value ? "rgba(59,130,246,0.2)" : "var(--bg-neutral)"}`,
                          borderRadius: 10, padding: "0.7rem 0.5rem", cursor: "pointer",
                          background: form.intent === value ? "var(--accent-subtle)" : "rgba(255,255,255,0.5)",
                          textAlign: "center", transition: "all 200ms ease",
                        }}>
                        <Icon size={15} color={form.intent === value ? "var(--accent)" : "var(--text-muted)"} strokeWidth={1.5} style={{ margin: "0 auto 4px" }} />
                        <div style={{ fontSize: "0.7rem", fontWeight: 600, color: form.intent === value ? "var(--accent-text)" : "var(--text-secondary)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.3 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontFamily: "'Manrope', sans-serif", marginTop: 6, textAlign: "center" }}>
                    You can always do everything — this just helps us guide you.
                  </p>
                </div>

                <button type="submit" data-testid="register-submit-btn" className="btn-primary" disabled={loading}
                  style={{ width: "100%", padding: "0.7rem", fontSize: "0.88rem", borderRadius: 10, opacity: loading ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  {loading ? <><Loader2 size={15} className="spin" /> Sending code...</> : "Send verification code"}
                </button>
              </motion.form>
            ) : (
              <motion.div key="otp-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: "1.5rem" }} onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                      data-testid={`register-otp-input-${i}`}
                      type="text" inputMode="numeric" maxLength={1}
                      value={digit} onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      style={{
                        width: 46, height: 54, textAlign: "center", fontSize: "1.3rem", fontWeight: 700,
                        fontFamily: "'Manrope', sans-serif", borderRadius: 10, border: "1.5px solid var(--bg-neutral)",
                        background: digit ? "var(--accent-subtle)" : "rgba(255,255,255,0.8)",
                        color: "var(--text-main)", outline: "none", transition: "border 200ms, background 200ms",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "var(--accent)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "var(--bg-neutral)"; }}
                    />
                  ))}
                </div>

                <button data-testid="register-verify-btn" className="btn-primary" disabled={loading || otp.some((d) => !d)}
                  onClick={() => verifyOTP(otp.join(""))}
                  style={{ width: "100%", padding: "0.7rem", fontSize: "0.88rem", borderRadius: 10, opacity: loading || otp.some((d) => !d) ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: "1rem" }}>
                  {loading ? <><Loader2 size={15} className="spin" /> Verifying...</> : "Verify & create account"}
                </button>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button data-testid="register-back-btn" onClick={() => { setStep("info"); setOtp(["", "", "", "", "", ""]); setError(""); }}
                    style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}>
                    <ArrowLeft size={12} /> Back
                  </button>
                  <button data-testid="register-resend-btn" onClick={resend} disabled={cooldown > 0}
                    style={{ fontSize: "0.78rem", color: cooldown > 0 ? "var(--text-muted)" : "var(--accent)", fontWeight: 600, background: "none", border: "none", cursor: cooldown > 0 ? "default" : "pointer", fontFamily: "'Manrope', sans-serif" }}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ textAlign: "center", marginTop: "1.4rem" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontFamily: "'Manrope', sans-serif" }}>Already have an account? </span>
            <Link to="/login" style={{ fontSize: "0.8rem", color: "var(--accent)", fontWeight: 600, fontFamily: "'Manrope', sans-serif", textDecoration: "none" }}>Sign in</Link>
          </div>
        </div>
      </motion.div>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
